/**
 * PartyBasicInfo
 *
 * Name, trade name, account code, party roles, country, and vendor-code fields.
 */

import { useCallback } from 'react';

import { cn } from '@elsapiens/utils';

import { getCountryByCode } from '../../../services/geolocation-service';
import { ROLE_CUSTOMER, PARTY_ROLES } from '../../../types/billing';
import { rolesToPartyType } from '../../../utils/billing-helpers';
import { firstNonBlank } from '../../../utils/formatters';
import {
  FormField,
  Input,
  CountrySelect,
} from '../../ui';

import type { PartyFormData } from './party-dialog-helpers';

export type RoleOption = { value: string; label: string }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartyBasicInfoProps {
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
  errors: Record<string, string>;
  selectedEntity: { id: string } | null;
  editingParty: { id: string } | null | undefined;
  baseCurrency: string;
  availableRoles?: RoleOption[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CountryChangeInput {
  setFormData: PartyBasicInfoProps['setFormData']
  editingParty: PartyBasicInfoProps['editingParty']
  baseCurrency: string
}

function useCountryChangeHandler({ setFormData, editingParty, baseCurrency }: CountryChangeInput): (code: string | null, name: string | null) => void {
  return useCallback((code: string | null, name: string | null): void => {
    void (async () => {
      const selectedCode = code ?? 'IN';
      const country = await getCountryByCode(selectedCode);
      const telephone = country?.telephone ?? '+91';
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          countryCode: selectedCode,
          country: name ?? 'India',
          stateCode: '',
          state: '',
        },
        phone: prev.phone
          ? { ...prev.phone, countryCode: selectedCode, telephone, full: telephone + prev.phone.number }
          : { countryCode: selectedCode, telephone, number: '', full: telephone },
        mobile: prev.mobile
          ? { ...prev.mobile, countryCode: selectedCode, telephone, full: telephone + prev.mobile.number }
          : { countryCode: selectedCode, telephone, number: '', full: telephone },
        defaultCurrency: editingParty
          ? prev.defaultCurrency
          : (firstNonBlank(country?.currencyCode, prev.defaultCurrency) ?? baseCurrency),
      }));
    })();
  }, [setFormData, editingParty, baseCurrency]);
}

interface RoleSectionProps {
  formData: PartyBasicInfoProps['formData']
  roleOptions: readonly RoleOption[]
  errors: PartyBasicInfoProps['errors']
  onRoleChange: (role: string, checked: boolean) => void
}

function RoleSelector({ formData, roleOptions, errors, onRoleChange }: RoleSectionProps): JSX.Element {
  return (
    <div aria-labelledby="party-roles-label" className="col-span-2" role="group">
      <span className="block text-xs font-medium text-foreground mb-1.5" id="party-roles-label">
        Party Roles <span className="text-destructive">*</span>
      </span>
      <div className="el-bleed flex items-center gap-1.5 flex-wrap">
        {(roleOptions as RoleOption[]).map((role: RoleOption) => {
          const isChecked = (formData.roles ?? []).includes(role.value)
          return (
            <button
              key={role.value}
              className={cn(
                'px-2.5 py-0.5 rounded-full border text-xs font-medium transition-colors select-none',
                isChecked
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background text-foreground hover:bg-muted',
              )}
              type="button"
              onClick={() => onRoleChange(role.value, !isChecked)}
            >
              {role.label}
            </button>
          )
        })}
      </div>
      {errors.roles && <p className="text-xs text-destructive mt-1">{errors.roles}</p>}
    </div>
  )
}

export function PartyBasicInfo({
  formData,
  setFormData,
  errors,
  selectedEntity,
  editingParty,
  baseCurrency,
  availableRoles,
}: PartyBasicInfoProps): JSX.Element {
  const roleOptions: readonly RoleOption[] = availableRoles ?? PARTY_ROLES

  const handleCountryChange = useCountryChangeHandler({ setFormData, editingParty, baseCurrency })
  const handleRoleChange = useCallback((role: string, checked: boolean): void => {
    setFormData(prev => {
      const newRoles = checked
        ? [...(prev.roles ?? []).filter(r => r !== role), role]
        : (prev.roles ?? []).filter(r => r !== role);
      return { ...prev, roles: newRoles, partyType: rolesToPartyType(newRoles) };
    });
  }, [setFormData]);

  return (
    <div>
      <h3 className="text-base font-semibold text-foreground el-section-heading el-mb-sm border-b border-border">
        Basic Information
      </h3>
      <div className="grid grid-cols-2 el-gap-field el-mt-sm">
        <CodeAndCountryRow
          editingParty={editingParty}
          errors={errors}
          formData={formData}
          selectedEntity={selectedEntity}
          setFormData={setFormData}
          onCountryChange={handleCountryChange}
        />
        <LegalAndTradeNameRow errors={errors} formData={formData} selectedEntity={selectedEntity} setFormData={setFormData} />
        <RoleSelector errors={errors} formData={formData} roleOptions={roleOptions} onRoleChange={handleRoleChange} />
        {formData.roles.includes(ROLE_CUSTOMER) && (
          <VendorCodeField formData={formData} setFormData={setFormData} />
        )}
      </div>
    </div>
  );
}

interface CodeAndCountryRowProps {
  errors: PartyBasicInfoProps['errors']
  formData: PartyBasicInfoProps['formData']
  setFormData: PartyBasicInfoProps['setFormData']
  selectedEntity: PartyBasicInfoProps['selectedEntity']
  editingParty: PartyBasicInfoProps['editingParty']
  onCountryChange: (code: string | null, name: string | null) => void
}

function CodeAndCountryRow({ errors, formData, setFormData, selectedEntity, editingParty, onCountryChange }: CodeAndCountryRowProps): JSX.Element {
  return (
    <>
      <FormField compact required label="Account Code" {...(errors.code ? { error: errors.code } : {})}>
        <Input
          disabled={!!editingParty}
          placeholder="e.g., C001, V001"
          value={formData.code}
          onChange={e => setFormData(prev => ({ ...prev, code: e.target.value }))}
        />
      </FormField>
      <FormField compact hideMessage required label="Country">
        <CountrySelect
          hideMessage
          disabled={!!selectedEntity}
          placeholder="Select country"
          value={formData.billingAddress.countryCode ?? 'IN'}
          onChange={onCountryChange}
        />
      </FormField>
    </>
  )
}

interface LegalAndTradeNameRowProps {
  errors: PartyBasicInfoProps['errors']
  formData: PartyBasicInfoProps['formData']
  setFormData: PartyBasicInfoProps['setFormData']
  selectedEntity: PartyBasicInfoProps['selectedEntity']
}

function LegalAndTradeNameRow({ errors, formData, setFormData, selectedEntity }: LegalAndTradeNameRowProps): JSX.Element {
  return (
    <>
      <FormField compact required label="Legal Name" {...(errors.entityName ? { error: errors.entityName } : {})}>
        <Input
          disabled={!!selectedEntity}
          placeholder="Full legal name"
          value={formData.entityName}
          onChange={e => setFormData(prev => ({ ...prev, entityName: e.target.value }))}
        />
      </FormField>
      <FormField compact label="Trade Name">
        <Input
          placeholder="Trading or brand name"
          value={formData.tradeName ?? ''}
          onChange={e => setFormData(prev => ({ ...prev, tradeName: e.target.value }))}
        />
      </FormField>
    </>
  )
}

function VendorCodeField({ formData, setFormData }: Pick<PartyBasicInfoProps, 'formData' | 'setFormData'>): JSX.Element {
  return (
    <FormField compact helperText="Code assigned to you in their vendor system" label="Vendor Code at Customer">
      <Input
        maxLength={100}
        placeholder="e.g., VENDOR-123"
        value={formData.companyVendorCode ?? ''}
        onChange={e => setFormData(prev => ({ ...prev, companyVendorCode: e.target.value }))}
      />
    </FormField>
  )
}
